import { useEffect, useRef } from "react";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/**
 * Admin paneli genelindeki önemli olaylar için sesli/polling alarm.
 *
 * Layout'a mount edilir → admin hangi sayfada olursa olsun çalışır. Belirli
 * aralıkla (POLL_MS) aşağıdaki sayaçları çeker; sayı bir ÖNCEKİ kontrolden
 * FAZLAYSA (yeni olay geldi) sesli uyarı + toast gösterir:
 *   - Onay bekleyen DEĞERLEME ilanları
 *   - Onay bekleyen NORMAL ilanlar
 *   - Başlayacak (yeni) MEZATlar
 *
 * Ayrıca admin panelinde herhangi bir yerde toastr ile toast gösterildiğinde
 * de aynı zil sesi çalar (onShown callback).
 *
 * Ses: harici dosya yerine Web Audio API ile zil melodisi üretilir
 * (binary asset gerekmez). Tarayıcı autoplay politikası gereği ses ancak
 * kullanıcı sayfayla bir kez etkileşince çalar; etkileşim yoksa sessizce toast atılır.
 */

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";
const POLL_MS = 60000; // 60 sn
const STORAGE_KEY = "pendingValuationsLastSeen";
const STORAGE_KEY_ILAN = "pendingNormalAdsLastSeen";
const STORAGE_KEY_MEZAT = "upcomingMezatsLastSeen";

const PendingApprovalAlarm = () => {
  const lastCountRef = useRef(null);   // bekleyen değerleme
  const lastIlanRef = useRef(null);    // bekleyen normal ilan
  const lastMezatRef = useRef(null);   // başlayacak (yeni) mezat
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    const token = localStorage.getItem("token") || authUser?.Token;
    const isAdmin = authUser?.HESAP_TURU === "Admin";
    if (!isAdmin || !token) return; // yalnız adminlerde

    // İlk açılışta alarm çalmasın diye son görülen değerleri sessionStorage'dan al.
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) lastCountRef.current = parseInt(stored, 10);
    const storedIlan = sessionStorage.getItem(STORAGE_KEY_ILAN);
    if (storedIlan !== null) lastIlanRef.current = parseInt(storedIlan, 10);
    const storedMezat = sessionStorage.getItem(STORAGE_KEY_MEZAT);
    if (storedMezat !== null) lastMezatRef.current = parseInt(storedMezat, 10);

    toastr.options = {
      closeButton: true,
      positionClass: "toast-top-right",
      timeOut: 15000,          // Toast 15 saniye kalıcı
      extendedTimeOut: 5000,   // Üzerine gelindiğinde 5 sn daha uzat
      preventDuplicates: true,
      escapeHtml: false,
      // Tema override'larına karşı okunaklı renkler zorla
      toastClass: "toast",
      titleClass: "toast-title text-white fw-bold",
      messageClass: "toast-message text-white",
      // Admin panelinde HER toastr toast'u gösterildiğinde zil sesi çalsın
      onShown: () => playBeep(),
    };

    // Global toast stillerini zorla (tema override varsa üzerine yaz)
    const ensureToastStyles = () => {
      const id = "pending-alarm-toast-override";
      if (document.getElementById(id)) return;
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        .toast { opacity: 1 !important; }
        .toast-info { background-color: #0d6efd !important; background-image: none !important; }
        .toast-title, .toast-message { color: #fff !important; text-shadow: none !important; }
        .toast-close-button { color: #fff !important; opacity: 0.8 !important; }
      `;
      document.head.appendChild(style);
    };
    ensureToastStyles();

    const playBeep = async () => {
      try {
        if (!audioCtxRef.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (!Ctx) return;
          audioCtxRef.current = new Ctx();
        }
        const ctx = audioCtxRef.current;
        // Tarayıcı autoplay politikası nedeniyle suspended ise resume et
        if (ctx.state === "suspended") {
          await ctx.resume();
        }

        const now = ctx.currentTime;
        const master = ctx.createGain();
        master.gain.setValueAtTime(1.0, now); // maksimum gain
        master.connect(ctx.destination);

        // Daha duyulur, yüksek frekanslı, tekrarlayan alarm zili
        const rings = [
          { freq: 2093, dur: 0.18, pause: 0.12 }, // C7
          { freq: 2093, dur: 0.18, pause: 0.12 },
          { freq: 2093, dur: 0.18, pause: 0.12 },
          { freq: 2093, dur: 0.18, pause: 0.12 },
          { freq: 2637, dur: 0.35, pause: 0.15 }, // E7 (son vurgu)
          { freq: 2093, dur: 0.35, pause: 0 },    // C7 (son vurgu)
        ];

        let t = now;
        rings.forEach((ring) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          // Square wave sine'dan daha keskin ve duyulur
          osc.type = "square";
          osc.frequency.setValueAtTime(ring.freq, t);

          gain.gain.setValueAtTime(0.001, t);
          gain.gain.exponentialRampToValueAtTime(1.0, t + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.001, t + ring.dur);

          osc.connect(gain);
          gain.connect(master);
          osc.start(t);
          osc.stop(t + ring.dur);
          t += ring.dur + ring.pause;
        });
      } catch {
        /* ses çalınamadı → sessiz geç */
      }
    };

    let cancelled = false;

    // Tek bir sayaç metriğini çeker; bir önceki ölçümden fazlaysa alarm + toast verir
    // ve ilgili sayfanın listesini yenilemesi için global event fırlatır.
    const checkMetric = async (filter, storageKey, ref, title, makeBody, eventType) => {
      try {
        const url = `${BASE_URL}/${filter.path}?$filter=${encodeURIComponent(filter.expr)}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          console.warn(`[PendingApprovalAlarm] ${filter.path} isteği başarısız:`, res.status);
          return;
        }
        const text = await res.text();
        const count = parseInt(text, 10);
        console.log(`[PendingApprovalAlarm] ${eventType || filter.path} count=${count} prev=${ref.current}`);
        if (cancelled || !Number.isFinite(count)) return;

        const prev = ref.current;
        if (prev !== null && count > prev) {
          console.log(`[PendingApprovalAlarm] Yeni olay: ${eventType}, artış=${count - prev}`);
          playBeep();
          toastr.info(makeBody(count - prev, count), title);
          // İlgili sayfada açıkken listeyi otomatik yenilemesi için event gönder
          if (eventType) {
            window.dispatchEvent(new CustomEvent("pending-alarm:new-event", { detail: { type: eventType } }));
          }
        }
        ref.current = count;
        sessionStorage.setItem(storageKey, String(count));
      } catch (err) {
        console.error("[PendingApprovalAlarm] checkMetric hata:", err);
      }
    };

    const check = () => {
      // 1) Onay bekleyen değerleme
      checkMetric(
        { path: "ILANLAR/$count", expr: "AKTIF_DURUM eq 'Beklemede' and DEGERLEMEYE_ACIK_MI eq true" },
        STORAGE_KEY, lastCountRef, "🔔 Yeni Değerleme",
        (yeni, toplam) => `${yeni} yeni değerleme onay bekliyor (toplam ${toplam}). İlan Yönetimi'nde inceleyin.`,
        "VALUATION"
      );
      // 2) Onay bekleyen normal ilan (satış/teklif ilanları)
      checkMetric(
        { path: "ILANLAR/$count", expr: "AKTIF_DURUM eq 'Beklemede' and DEGERLEMEYE_ACIK_MI ne true" },
        STORAGE_KEY_ILAN, lastIlanRef, "📢 Yeni İlan",
        (yeni, toplam) => `${yeni} yeni ilan onay bekliyor (toplam ${toplam}). İlan Yönetimi'nde inceleyin.`,
        "AD"
      );
      // 3) Onay bekleyen mezat (DURUM_ID=5). Yeni mezat artık doğrudan
      //    yayın takvimine girmiyor; onaylanınca 1 (Başlayacak) olur.
      checkMetric(
        { path: "MEZATLAR/$count", expr: "DURUM_ID eq 5" },
        STORAGE_KEY_MEZAT, lastMezatRef, "🔥 Onay Bekleyen Mezat",
        (yeni, toplam) => `${yeni} yeni mezat onay bekliyor (toplam ${toplam}). Mezat Yönetimi'nden onaylayın.`,
        "MEZAT"
      );
    };

    check(); // hemen bir kez
    const id = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return null; // görsel çıktısı yok
};

export default PendingApprovalAlarm;
